import React, { useState, useEffect } from "react";
import {
    Box,
    Typography,
    Checkbox,
    FormControlLabel,
    FormGroup,
    RadioGroup,
    Radio,
    TextField,
    Button,
    Chip,
    Grid,
    Paper,
} from "@mui/material";
import { Add as AddIcon } from "@mui/icons-material";

const DEFAULT_TEST_POINTS = [
    "North Wall",
    "South Wall",
    "East Wall",
    "West Wall",
    "Door",
    "Control Window",
    "Pen Panel",
    "Magnet Access",
];

/**
 * Parses existing test points that may already have polarization suffixes.
 * Returns { basePoints, polarization }
 */
function parseTestPoints(points) {
    if (!points || points.length === 0) {
        return { basePoints: [], polarization: "vertical" };
    }
    const hasVertical = points.some((p) => p.endsWith(" - Vertical"));
    const hasHorizontal = points.some((p) => p.endsWith(" - Horizontal"));

    let polarization = "both";
    if (hasVertical && !hasHorizontal) polarization = "vertical";
    else if (hasHorizontal && !hasVertical) polarization = "horizontal";
    else if (hasVertical && hasHorizontal) polarization = "both";

    const basePoints = points
        .map((p) => p.replace(" - Vertical", "").replace(" - Horizontal", ""))
        .filter((p, i, arr) => arr.indexOf(p) === i); // dedupe

    return { basePoints, polarization };
}

function generateWithPolarization(points, polarization) {
    if (polarization === "vertical")
        return points.map((p) => `${p} - Vertical`);
    if (polarization === "horizontal")
        return points.map((p) => `${p} - Horizontal`);
    // both
    const result = [];
    points.forEach((p) => {
        result.push(`${p} - Vertical`);
        result.push(`${p} - Horizontal`);
    });
    return result;
}

export default function TestPointSelector({ testPoints, onChange }) {
    const { basePoints, polarization: initPol } = parseTestPoints(testPoints);

    const initCustom = basePoints.filter(
        (p) => !DEFAULT_TEST_POINTS.includes(p),
    );

    const [selected, setSelected] = useState(basePoints);
    const [polarization, setPolarization] = useState(initPol || "vertical");
    const [customPoints, setCustomPoints] = useState(initCustom);
    const [newPoint, setNewPoint] = useState("");

    // Notify parent whenever selection or polarization changes
    useEffect(() => {
        onChange(generateWithPolarization(selected, polarization));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selected, polarization]);

    const allPoints = [...DEFAULT_TEST_POINTS, ...customPoints];

    const handleToggle = (point) => {
        setSelected((prev) =>
            prev.includes(point)
                ? prev.filter((p) => p !== point)
                : [...prev, point],
        );
    };

    const handleCheckAll = () => {
        if (selected.length === allPoints.length) {
            setSelected([]);
        } else {
            setSelected([...allPoints]);
        }
    };

    const handleAddCustom = () => {
        const trimmed = newPoint.trim();
        if (!trimmed || allPoints.includes(trimmed)) return;
        setCustomPoints((prev) => [...prev, trimmed]);
        setSelected((prev) => [...prev, trimmed]);
        setNewPoint("");
    };

    const handleRemoveCustom = (point) => {
        setCustomPoints((prev) => prev.filter((p) => p !== point));
        setSelected((prev) => prev.filter((p) => p !== point));
    };

    const previewPoints = generateWithPolarization(selected, polarization);

    return (
        <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Select which walls/locations to test, then choose polarization.
            </Typography>

            <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            mb: 0.5,
                        }}
                    >
                        <Typography variant="subtitle2">
                            Test Locations
                        </Typography>
                        <Button
                            size="small"
                            variant="text"
                            onClick={handleCheckAll}
                        >
                            {selected.length === allPoints.length
                                ? "Uncheck All"
                                : "Check All"}
                        </Button>
                    </Box>
                    <Paper variant="outlined" sx={{ p: 1.5 }}>
                        <FormGroup>
                            {allPoints.map((point) => (
                                <Box
                                    key={point}
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "space-between",
                                    }}
                                >
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={selected.includes(
                                                    point,
                                                )}
                                                onChange={() =>
                                                    handleToggle(point)
                                                }
                                                size="small"
                                            />
                                        }
                                        label={
                                            <Typography variant="body2">
                                                {point}
                                            </Typography>
                                        }
                                    />
                                    {customPoints.includes(point) && (
                                        <Chip
                                            label="Remove"
                                            size="small"
                                            color="error"
                                            variant="outlined"
                                            onClick={() =>
                                                handleRemoveCustom(point)
                                            }
                                            sx={{ mr: 1 }}
                                        />
                                    )}
                                </Box>
                            ))}
                        </FormGroup>
                    </Paper>

                    <Box sx={{ display: "flex", gap: 1, mt: 1.5 }}>
                        <TextField
                            size="small"
                            label="Custom location"
                            value={newPoint}
                            onChange={(e) => setNewPoint(e.target.value)}
                            onKeyDown={(e) =>
                                e.key === "Enter" && handleAddCustom()
                            }
                            sx={{ flex: 1 }}
                            placeholder="e.g. RF Penetration Panel"
                        />
                        <Button
                            variant="outlined"
                            startIcon={<AddIcon />}
                            onClick={handleAddCustom}
                            disabled={!newPoint.trim()}
                        >
                            Add
                        </Button>
                    </Box>
                </Grid>

                <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" gutterBottom>
                        Polarization
                    </Typography>
                    <Paper variant="outlined" sx={{ p: 1.5, mb: 2 }}>
                        <RadioGroup
                            value={polarization}
                            onChange={(e) => setPolarization(e.target.value)}
                        >
                            <FormControlLabel
                                value="both"
                                control={<Radio size="small" />}
                                label={
                                    <Typography variant="body2">
                                        Both (Vertical &amp; Horizontal)
                                    </Typography>
                                }
                            />
                            <FormControlLabel
                                value="vertical"
                                control={<Radio size="small" />}
                                label={
                                    <Typography variant="body2">
                                        Vertical only
                                    </Typography>
                                }
                            />
                            <FormControlLabel
                                value="horizontal"
                                control={<Radio size="small" />}
                                label={
                                    <Typography variant="body2">
                                        Horizontal only
                                    </Typography>
                                }
                            />
                        </RadioGroup>
                    </Paper>

                    <Typography variant="subtitle2" gutterBottom>
                        Preview ({previewPoints.length} test points)
                    </Typography>
                    <Paper
                        variant="outlined"
                        sx={{ p: 1.5, maxHeight: 220, overflowY: "auto" }}
                    >
                        {previewPoints.length === 0 ? (
                            <Typography variant="body2" color="text.secondary">
                                No test points selected
                            </Typography>
                        ) : (
                            <Box
                                sx={{
                                    display: "flex",
                                    flexWrap: "wrap",
                                    gap: 0.5,
                                }}
                            >
                                {previewPoints.map((p) => (
                                    <Chip key={p} label={p} size="small" />
                                ))}
                            </Box>
                        )}
                    </Paper>
                </Grid>
            </Grid>
        </Box>
    );
}
